import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

// #design-pass: Theme-System (Light Default + Dark-Toggle). Setzt data-theme auf
// <html>, persistiert in localStorage. Theming läuft über CSS-Variablen
// ([data-theme="light|dark"] in index.css), nicht über Tailwind dark:-Varianten.
type Theme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function initialTheme(): Theme {
  const saved = typeof localStorage !== "undefined" ? localStorage.getItem("am-theme") : null;
  return saved === "dark" || saved === "light" ? saved : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("am-theme", theme);
  }, [theme]);

  function toggleTheme() {
    // Transitions kurz unterdrücken, damit der Farbwechsel nicht "hängt".
    document.documentElement.setAttribute("data-theme-switching", "");
    setTheme((t) => (t === "light" ? "dark" : "light"));
    requestAnimationFrame(() =>
      requestAnimationFrame(() => document.documentElement.removeAttribute("data-theme-switching")),
    );
  }

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
